import { green } from "@ant-design/colors"
import { faAdd, faCheck, faEdit, faLink, faSave, faTimes, faUnlink } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Form, Input, Popconfirm, Select, Space, Switch, Table, TableColumnType, Typography } from "antd"
import { useState } from "react"
import { useActiveUsers } from "../../../domain/users/userQueries"
import { hasValue } from "../../../helpers/nullable"
import VSpace from "../../common/VSpace"
import ThemableButton from "../../Items/ThemableButton"
import ToggleablePopconfirm from "../Divisions/ToggleablePopconfirm"
import { CrewMember } from "./crewMemberApi"
import { useCrewMembers, useUpdateCrewMember } from "./crewMemberQueries"
import LinkCrewMemberModal from "./LinkCrewMemberModal"

const NEW_CREW_MEMBER_ID = -1
const CrewMembers = () => {
  const crewMembersQuery = useCrewMembers()
  const updateCrewMemberMutation = useUpdateCrewMember()
  const activeUsersQuery = useActiveUsers()

  const [form] = Form.useForm()
  const [editingCrewMemberId, setEditingCrewMemberId] = useState<number | undefined>()
  const editingCrewMember = crewMembersQuery.data?.find(crewMember => crewMember.id === editingCrewMemberId)
  const editingCrewMemberQboId = Form.useWatch('qboId', form)
  const editingCrewMemberQboDisplayName = Form.useWatch('qboDisplayName', form)

  const [addedCrewMember, setAddedCrewMember] = useState<CrewMember[]>([])
  const crewMembers = [...addedCrewMember, ...(crewMembersQuery.data ?? [])]

  const shouldConfirmAdd = () => hasValue(editingCrewMemberId) && form.isFieldsTouched()
  const shouldConfirmEdit = (id: number) => hasValue(editingCrewMemberId) && editingCrewMemberId !== id && form.isFieldsTouched()
  const shouldConfirmCancel = () => form.isFieldsTouched()

  const [showLinkCrewMemberModal, setShowLinkCrewMemberModal] = useState(false)

  const handleAdd = () => {
    const newCrewMember = {
      id: NEW_CREW_MEMBER_ID,
      name: '',
      email: null,
      userEmail: null,
      isActive: true,
      timesheetNotifications: false,
      qboDisplayName: null,
      qboId: null,
    }
    setAddedCrewMember([newCrewMember])
    form.setFieldsValue(newCrewMember)
    setEditingCrewMemberId(NEW_CREW_MEMBER_ID)
  }

  const editCrewMember = (id: number) => {
    if (editingCrewMemberId === id) {
      cancelEdit()
    }
    setEditingCrewMemberId(id)
    setTimeout(() => form.setFieldsValue(crewMembers.find(x => x.id === id)), 1)
  }

  const cancelEdit = () => {
    if (editingCrewMemberId === NEW_CREW_MEMBER_ID) {
      setAddedCrewMember([])
    }
    setEditingCrewMemberId(undefined)
    form.resetFields()
  }

  const saveCrewMember = async () => {
    try {
      await form.validateFields()
      updateCrewMemberMutation.mutate(form.getFieldsValue())
      setEditingCrewMemberId(undefined)
      setAddedCrewMember([])
      form.resetFields()
    } catch { }
  }

  const handleLinkCrewMember = (qboId?: string, qboDisplayName?: string) => {
    if (qboId && qboDisplayName) {
      form.setFieldsValue({ qboId, qboDisplayName })
    }
    setShowLinkCrewMemberModal(false)
  }

  const columns: TableColumnType<CrewMember>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      render: (value, record) => {
        if (record.id === editingCrewMemberId) {
          return <Form.Item name="name">
            <Input />
          </Form.Item>
        }

        return value
      }
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      render: (value, record) => {
        if (record.id === editingCrewMemberId) {
          return <Form.Item name="email">
            <Input type="email" maxLength={65} />
          </Form.Item>
        }

        return value
      }
    },
    {
      key: 'userEmail',
      title: 'User Account',
      dataIndex: 'userEmail',
      render: (value, record) => {
        if (record.id === editingCrewMemberId) {
          const userOptions = (activeUsersQuery.data ?? [])
            .filter(user => user.email === value || !crewMembers.some(crewMember => crewMember.userEmail === user.email))
            .map(user => ({
              label: `${user.displayName} (${user.email})`,
              value: user.email
            }))
          return <Form.Item name="userEmail">
            <Select options={userOptions} value={value} allowClear />
          </Form.Item>
        }
        const user = activeUsersQuery.data?.find(user => user.email === value)
        return user ? `${user.displayName} (${user.email})` : ''
      }
    },
    {
      key: 'isActive',
      title: 'Active',
      dataIndex: 'isActive',
      align: 'center',
      render: (value, record) => {
        if (record.id === editingCrewMemberId) {
          return <Form.Item name="isActive" valuePropName="checked">
            <Switch checked={value} />
          </Form.Item>
        }

        return value ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faTimes} />
      }
    },
    {
      key: 'timesheetNotifications',
      title: 'Timesheet Notifications',
      dataIndex: 'timesheetNotifications',
      align: 'center',
      render: (value, record) => {
        if (record.id === editingCrewMemberId) {
          return <Form.Item name="timesheetNotifications" valuePropName="checked">
            <Switch checked={value} />
          </Form.Item>
        }

        return value ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faTimes} />
      }
    },
    {
      key: 'qboDisplayName',
      title: 'QuickBooks Online Entry',
      render: (record) => {
        if (record.id === editingCrewMemberId) {
          return <>
            <Form.Item>
              <Space>
                {editingCrewMemberQboId
                  ? <Button type="link">{editingCrewMemberQboDisplayName}</Button>
                  : <ThemableButton
                    type="primary"
                    icon={<FontAwesomeIcon icon={faLink} />}
                    colorPrimary={green.primary}
                    onClick={() => setShowLinkCrewMemberModal(true)}
                  >
                    Link to QuickBooks
                  </ThemableButton>
                }
                {editingCrewMemberQboId &&
                  <Popconfirm
                    title="Are you sure you want to unlink this crew member from QuickBooks?"
                    onConfirm={() => form.setFieldsValue({ qboId: undefined, qboDisplayName: undefined })}
                  >
                    <Button
                      type="link"
                      danger
                      icon={<FontAwesomeIcon icon={faUnlink} />}
                    />
                  </Popconfirm>
                }
              </Space>
            </Form.Item>
            <Form.Item hidden name="qboId">
              <Input type="hidden" />
            </Form.Item>
            <Form.Item hidden name="qboDisplayName">
              <Input type="hidden" />
            </Form.Item>
          </>
        }

        return record.qboDisplayName ?? <Typography.Text type="danger">Not linked</Typography.Text>
      }
    },
    {
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        if (record.id === editingCrewMemberId) {
          return <Form.Item>
            <Space>
              <Button
                type="link"
                onClick={saveCrewMember}
                icon={<FontAwesomeIcon icon={faSave} />}
              />
              <ToggleablePopconfirm
                title="Are you sure you want to discard your changes?"
                shouldConfirm={shouldConfirmCancel}
                onConfirm={cancelEdit}
              >
                <Button
                  type="link"
                  danger
                  icon={<FontAwesomeIcon icon={faTimes} />}
                />
              </ToggleablePopconfirm>
            </Space>
          </Form.Item>
        }

        return <ToggleablePopconfirm
          title="Another crew member is being edited. Are you sure you want to edit this one?"
          shouldConfirm={() => shouldConfirmEdit(record.id)}
          onConfirm={() => editCrewMember(record.id)}
        >
          <Button
            type="link"
            icon={<FontAwesomeIcon icon={faEdit} />}
          />
        </ToggleablePopconfirm>
      }
    }
  ]

  return <>
    <VSpace>
      <ToggleablePopconfirm
        title="Another item is already being edited. Do you want to discard those changes?"
        shouldConfirm={shouldConfirmAdd}
        onConfirm={handleAdd}
      >
        <Button
          icon={<FontAwesomeIcon icon={faAdd} />}
          type="primary"
        >
          Add Crew Member
        </Button>
      </ToggleablePopconfirm>
      <Form initialValues={editingCrewMember} form={form}>
        <Form.Item hidden name="id">
          <Input type="hidden" />
        </Form.Item>
        <Table
          rowKey="id"
          loading={crewMembersQuery.isLoading}
          columns={columns}
          dataSource={crewMembers}
          pagination={false}
          onRow={(record) => {
            const isEditing = !!editingCrewMemberId
            return {
              onClick: !isEditing ? () => editCrewMember(record.id) : () => { },
              style: {
                cursor: !isEditing ? 'pointer' : 'default'
              }
            }
          }}
        />
      </Form>
    </VSpace>
    <LinkCrewMemberModal
      open={showLinkCrewMemberModal}
      onCancel={() => setShowLinkCrewMemberModal(false)}
      onSubmit={handleLinkCrewMember}
    />
  </>
}

export default CrewMembers
